/**
 * <pickup-availability>: shows local pickup information for the selected variant.
 * Markup (rendered by the buy buttons):
 *   <pickup-availability available? data-root-url data-variant-id>
 *     <template> error preview with a refresh button </template>
 *   </pickup-availability>
 * The preview + drawer come from /variants/{id}/?section_id=pickup-availability.
 */
(() => {
  const define = (tag, ctor) => {
    if (!window.customElements.get(tag)) window.customElements.define(tag, ctor);
  };

  class PickupAvailability extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.onRefresh = () => this.fetchAvailability(this.dataset.variantId);
      if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
    }

    sectionUrl(variantId) {
      const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      return `${root}variants/${variantId}/?section_id=pickup-availability`;
    }

    clearContent() {
      Array.from(this.children).forEach((child) => {
        if (child.tagName !== 'TEMPLATE') child.remove();
      });
    }

    fetchAvailability(variantId) {
      if (!variantId) return;
      this.dataset.variantId = variantId;
      const requested = String(variantId);
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      fetch(this.sectionUrl(variantId), { signal: this.controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`pickup request failed (${response.status})`);
          return response.text();
        })
        .then((markup) => {
          if (String(this.dataset.variantId) !== requested) return;
          const doc = new DOMParser().parseFromString(markup, 'text/html');
          this.renderPreview(doc.querySelector('.shopify-section') || doc.body);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          this.renderError();
        });
    }

    renderError() {
      this.clearContent();
      const template = this.querySelector(':scope > template');
      const fallback = template && template.content.firstElementChild;
      if (!fallback) return;
      const node = fallback.cloneNode(true);
      this.appendChild(node);
      this.setAttribute('available', '');
      const refresh = node.querySelector('button');
      if (refresh) refresh.addEventListener('click', this.onRefresh, { once: true });
    }

    renderPreview(scope) {
      document.querySelectorAll('body > pickup-availability-drawer').forEach((old) => old.remove());
      this.clearContent();

      const preview = scope && scope.querySelector('pickup-availability-preview');
      if (!preview) {
        this.removeAttribute('available');
        return;
      }
      this.appendChild(document.importNode(preview, true));
      this.setAttribute('available', '');

      const drawer = scope.querySelector('pickup-availability-drawer');
      if (drawer) document.body.appendChild(document.importNode(drawer, true));

      const opener = this.querySelector('button');
      if (opener) {
        opener.addEventListener('click', (event) => {
          const live = document.querySelector('body > pickup-availability-drawer');
          if (live && typeof live.show === 'function') live.show(event.currentTarget);
        });
      }
    }
  }

  class PickupAvailabilityDrawer extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.onOutsideClick = (event) => {
        if (event.target.closest('pickup-availability-drawer') || event.target.id === 'ShowPickupAvailabilityDrawer') return;
        this.hide();
      };
      const close = this.querySelector('.pickup-availability-drawer-button');
      if (close) close.addEventListener('click', () => this.hide());
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.hide();
      });
    }

    disconnectedCallback() {
      document.body.removeEventListener('click', this.onOutsideClick);
    }

    show(opener) {
      this.opener = opener || null;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      // attach after the current click finished bubbling
      setTimeout(() => document.body.addEventListener('click', this.onOutsideClick));
      if (typeof trapFocus === 'function') trapFocus(this);
      else this.focus();
    }

    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('click', this.onOutsideClick);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      else if (this.opener) this.opener.focus();
    }
  }

  define('pickup-availability', PickupAvailability);
  define('pickup-availability-drawer', PickupAvailabilityDrawer);
})();
